import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import Spinner from 'components/common/Spinner';
import GuideTooltip from 'components/common/GuideTooltip';
import { useTutorial, TutorialStep } from 'contexts/TutorialContext';
import { useTranslation } from 'react-i18next';

/**
 * Modal component for saving a new audience rule.
 *
 * @param {boolean} isOpen - Whether the modal is open.
 * @param {Function} onClose - Function to close the modal.
 * @param {Function} onSave - Function to save the rule with the provided rule name.
 * @param {string} selectedRuleName - Initial rule name to populate the input.
 */
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSave: (ruleName: string) => void;
  selectedRuleName: string;
}

interface ValidationError {
  type: 'error' | 'warning' | 'success';
  message: string;
}

const Modal: React.FC<Props> = ({ isOpen, onClose, onSave, selectedRuleName }) => {
  const { t } = useTranslation();
  const [ruleName, setRuleName] = useState<string>(selectedRuleName);
  const [validation, setValidation] = useState<ValidationError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Tutorial context and refs
  const { currentStep, isTutorialActive } = useTutorial();
  const ruleNameInputRef = useRef<HTMLInputElement>(null);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setRuleName(selectedRuleName);
      setValidation(null);
      setIsSubmitting(false);

      // Tutorial: Focus and scroll to input when at RULE_NAME_INPUT step
      if (isTutorialActive && currentStep === TutorialStep.RULE_NAME_INPUT) {
        setTimeout(() => {
          ruleNameInputRef.current?.focus();
          ruleNameInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 500);
      }
    }
  }, [isOpen, selectedRuleName, isTutorialActive, currentStep]);

  // Validation logic
  const validateRuleName = (name: string): ValidationError | null => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return { type: 'error', message: t('audienceSaveModal.errors.required') };
    }

    if (trimmedName.length < 3) {
      return { type: 'error', message: t('audienceSaveModal.errors.minLength') };
    }

    if (trimmedName.length > 50) {
      return { type: 'error', message: t('audienceSaveModal.errors.maxLength') };
    }

    if (!/^[\p{L}\p{N}\s\-_]+$/u.test(trimmedName)) {
      return { type: 'error', message: t('audienceSaveModal.errors.invalidCharacters') };
    }

    if (trimmedName.length >= 3 && trimmedName.length <= 50) {
      return { type: 'success', message: t('audienceSaveModal.messages.looksGood') };
    }

    return null;
  };

  // Handle input change with real-time validation
  const handleRuleNameChange = (value: string) => {
    setRuleName(value);
    setValidation(validateRuleName(value));
  };

  const handleSave = async () => {
    const currentValidation = validateRuleName(ruleName);

    if (currentValidation?.type === 'error') {
      setValidation(currentValidation);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSave(ruleName.trim());
      setRuleName("");
      onClose();
    } catch (error) {
      setValidation({ type: 'error', message: t('audienceSaveModal.errors.saveFailed') });
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSave = ruleName.trim().length >= 3 && validation?.type !== 'error' && !isSubmitting;

  const modalContent = (
    <div
      className={`famous-clicker-save-rule-modal-backdrop ${
        isOpen ? "famous-clicker-save-rule-show" : ""
      }`}
      onClick={onClose}
    >
      <div
        className="famous-clicker-save-rule-modal-container"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        <div className="famous-clicker-save-rule-modal-header">
          <div>
            <h2 className="famous-clicker-save-rule-modal-title">{t('audienceSaveModal.titles.main')}</h2>
            <p className="text-xs text-gray-600 mt-1">{t('audienceSaveModal.titles.subtitle')}</p>
          </div>
          <button
            className="famous-clicker-save-rule-modal-close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        <div className="famous-clicker-save-rule-modal-body">
          {/* Quick Tips */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start gap-2">
              <i className="bi bi-lightbulb text-blue-600 text-sm mt-0.5"></i>
              <div>
                <p className="text-xs font-semibold text-gray-800 mb-1">{t('audienceSaveModal.titles.namingTips')}</p>
                <p className="text-xs text-gray-700">{t('audienceSaveModal.messages.namingTipsDesc')}</p>
              </div>
            </div>
          </div>
          <div className="famous-clicker-save-rule-input-group">
            <input
              ref={ruleNameInputRef}
              type="text"
              className={`famous-clicker-save-rule-input-field ${
                validation?.type === 'error' ? 'error' : validation?.type === 'success' ? 'success' : ''
              }`}
              placeholder=" "
              id="ruleName"
              value={ruleName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleRuleNameChange(e.target.value)}
              maxLength={50}
              style={{
                boxShadow: isTutorialActive && currentStep === TutorialStep.RULE_NAME_INPUT
                  ? '0 0 0 4px rgba(99, 179, 237, 0.5)'
                  : undefined
              }}
            />
            <label className="famous-clicker-save-rule-input-label" htmlFor="ruleName">
              {t('audienceSaveModal.labels.ruleName')} *
            </label>

            {/* Character counter */}
            <div className="famous-clicker-save-rule-char-counter">
              {t('audienceSaveModal.labels.characters', { current: ruleName.length, max: 50 })}
            </div>
          </div>

          {/* Validation feedback */}
          {validation && (
            <div className={`famous-clicker-save-rule-validation-feedback ${validation.type}`}>
              <div className="famous-clicker-save-rule-validation-icon">
                {validation.type === 'error' && <AlertCircle size={16} />}
                {validation.type === 'success' && <CheckCircle size={16} />}
              </div>
              <span className="famous-clicker-save-rule-validation-message">
                {validation.message}
              </span>
            </div>
          )}

          {/* Rule guidelines */}
          <div className="famous-clicker-save-rule-guidelines">
            <h4>{t('audienceSaveModal.titles.guidelines')}</h4>
            <ul>
              <li>{t('audienceSaveModal.messages.guideline1')}</li>
              <li>{t('audienceSaveModal.messages.guideline2')}</li>
              <li>{t('audienceSaveModal.messages.guideline3')}</li>
            </ul>
          </div>
        </div>

        <div className="famous-clicker-save-rule-button-group">
          <button
            className="famous-clicker-save-rule-btn famous-clicker-save-rule-btn-secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {t('audienceSaveModal.buttons.cancel')}
          </button>
          <button
            className={`famous-clicker-save-rule-btn famous-clicker-save-rule-btn-primary ${
              !canSave ? 'disabled' : ''
            }`}
            onClick={handleSave}
            disabled={!canSave}
          >
            {isSubmitting ? (
              <>
                <Spinner size="small" color="white" />
                {t('audienceSaveModal.buttons.saving')}
              </>
            ) : (
              t('audienceSaveModal.buttons.saveRule')
            )}
          </button>
        </div>

        {/* Tutorial Tooltip */}
        <GuideTooltip
          targetRef={ruleNameInputRef}
          message={t('audienceSaveModal.messages.tutorialTooltip')}
          show={isTutorialActive && currentStep === TutorialStep.RULE_NAME_INPUT}
        />
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default Modal;